import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';
import { isArabicScript } from '@/utils/scriptConventions';
import { foundeloArticles } from '@/data/articles';
import core from '@/data/foundelo-core';

const SITE_NAME = 'وفّقنا';

// The wordmark, in the script the reader actually reads.
//
// The tab suffix was the Arabic wordmark for everyone, so a French member saw
// "Connexion | وفّقنا". A brand name is not translated — but this brand has an
// established Latin spelling, used on the Play listing and now in the localized
// PWA manifests, and that is the one to show where the rest of the chrome is
// Latin. Arabic members are unaffected.
//
// `og:site_name` deliberately keeps the Arabic form: share cards are scraped
// once, by a crawler with no member language, and Arabic is the primary market.
function siteName(language) {
  return isArabicScript(language) ? SITE_NAME : 'Wefaqna';
}
const BASE_URL = 'https://wefaqna.com';
const DEFAULT_IMAGE = `${BASE_URL}/og-image.png`;

// ---------------------------------------------------------------------------
// Share cards (og:image / twitter:image)
//
// TODAY: one 1.6 MB `/og-image.png` is the card for all ~121 pages. Every
// article shared on WhatsApp — the main share surface in Egypt — renders the
// identical generic tile, so a link to a bride-side guide and a link to the
// homepage look like the same link. `scripts/generate-seo-html.js` rewrites
// og:title / og:description / og:url per page but deliberately leaves og:image
// alone, so the pre-rendered HTML Googlebot and WhatsApp read carries the
// default too.
//
// The `image` prop below is the plumbing for fixing that. It is fully additive:
// any page that does not pass one keeps DEFAULT_IMAGE byte-for-byte, so no
// existing card, URL, canonical or ranking signal moves.
//
// ⚠️ THE IMAGES THEMSELVES DO NOT EXIST YET — THIS IS AN OWNER TASK.
// `frontend/public/` was checked: it holds only `og-image.png` (the generic
// default), `logo.png`, app icons/favicons, `guide-screenshots/` (product UI
// screenshots at arbitrary sizes — wrong aspect, unreadable at card scale) and
// `icons-2026-review/` (rejected 2026 brand-exploration SVGs; SVG is not
// rendered as an OG image by WhatsApp or Facebook at all). Nothing there is a
// usable 1200×630 card, and nothing was invented to fill the gap — shipping a
// reference to a file that is not in `public/` would give WhatsApp a 404 and
// leave those pages with NO card, which is worse than a generic one.
//
// WHAT TO PRODUCE — 5 cluster cards, dropped in `frontend/public/og/`:
//   1. og/bride.webp        — bride-side articles + /zawaj bride landings
//   2. og/groom.webp        — groom-side articles + groom landings
//   3. og/istikhara.webp    — istikhara / devotional / du'a articles
//   4. og/governorate.webp  — the /zawaj/<governorate> city pages
//   5. og/general.webp      — blog index, about, FAQ, everything else
//
// SPEC for each: exactly 1200 × 630 px, WebP, under 200 kB, Arabic text set
// large enough to read in a WhatsApp thumbnail (~400 px wide), brand colours
// per `specs/brand.md` — forest #083F22 ground, gold #D4AF37 accent. Keep the
// wordmark in a corner, not centred, so the crop different clients apply does
// not eat it. WebP is safe: WhatsApp, Facebook, X, Telegram and iMessage all
// render WebP OG images today.
//
// WIRING once the files land: pass `image="/og/bride.webp"` from the page (a
// leading-slash path is resolved against BASE_URL for you — OG requires an
// absolute URL), and mirror the same per-cluster mapping into
// `scripts/generate-seo-html.js` so the pre-rendered first-pass HTML agrees
// with what React renders. Until then every page keeps the current card.
// ---------------------------------------------------------------------------

/**
 * Resolve a share-card path to the absolute URL Open Graph requires.
 * Accepts a site-relative path ("/og/bride.webp") or an already-absolute URL.
 * Anything falsy falls back to the current default, so pages that pass nothing
 * are byte-identical to before.
 */
export function resolveShareImage(image) {
  if (!image) return DEFAULT_IMAGE;
  if (/^https?:\/\//i.test(image)) return image;
  return `${BASE_URL}${image.startsWith('/') ? '' : '/'}${image}`;
}

// ---------------------------------------------------------------------------
// hreflang
//
// The site had ZERO hreflang anywhere. That matters here because we serve
// three distinct content languages off one domain:
//   - Arabic  — everything by default (the whole Egyptian core of the site)
//   - English — the 3 diaspora landings + 9 English blog articles
//   - German  — 2 German blog articles
//
// It matters more than usual because `src/i18n/config.js` rewrites
// `document.documentElement.lang` to whatever UI locale the *visitor* picked.
// An English-UI visitor reading an Arabic article makes that page report
// lang="en". So <html lang> is not a trustworthy language signal on this site.
// A static hreflang self-reference in <head> is, and it is what Google reads.
//
// Two structures below, both additive — nothing here rewrites a URL, a
// canonical, a title or any body copy.
//
// 1. HREFLANG_CLUSTERS — real translation/regional-variant sets. A page must
//    list every member of its own set INCLUDING itself (hreflang is only
//    honoured when the annotations are reciprocal and self-inclusive).
// 2. PAGE_LANG — pages that are NOT part of a set but are not Arabic either.
//    They get an accurate self-reference so Google stops having to guess.
//
// Everything not listed in either falls back to DEFAULT_LANG ('ar'), which is
// true for every remaining route on the site.
//
// KEEP IN SYNC with the same two tables in `scripts/generate-seo-html.js`
// (that script bakes these tags into the pre-rendered first-pass HTML, which
// is the copy Googlebot reads before it runs any JavaScript).
// ---------------------------------------------------------------------------

const DEFAULT_LANG = 'ar';

// The three diaspora landings render the SAME component with the same layout
// and the same three USP slots — only the country name, tagline and USP copy
// differ. That is the textbook regional-variant duplicate-content shape, and
// it is exactly what hreflang exists to disambiguate. Without it Google picks
// one of the three near-identical pages and shows it in all three markets.
const HREFLANG_CLUSTERS = [
  {
    xDefault: '/muslim-marriage-usa',
    pages: [
      { hrefLang: 'en-GB', path: '/muslim-marriage-uk' },
      { hrefLang: 'en-US', path: '/muslim-marriage-usa' },
      // English-language page targeting Germany-based Muslims (the German
      // *language* articles are separate pages, listed in PAGE_LANG below).
      { hrefLang: 'en-DE', path: '/muslim-marriage-germany' },
    ],
  },
];

// TRUE translations, so these are real clusters rather than self-references.
// The three Arabic pillars carry this site (54,067 / 17,857 impressions in 180
// days), and each was written properly in each language rather than
// paraphrased — which is exactly the condition the note below sets for
// promoting a "missing pair" into a cluster.
//
// x-default points at the Arabic original: it is the highest-authority version
// and the language the majority of our searchers use.
const TRANSLATION_CLUSTERS = [
  // Articles published by Foundelo: language versions sharing a translation key.
  ...core.foundeloClusters(foundeloArticles),
  {
    xDefault: '/blog/al-istikhara-qabl-al-zawaj',
    pages: [
      { hrefLang: 'ar', path: '/blog/al-istikhara-qabl-al-zawaj' },
      { hrefLang: 'en', path: '/blog/istikhara-dua' },
      { hrefLang: 'de', path: '/blog/istikhara-gebet' },
      { hrefLang: 'fr', path: '/blog/douaa-istikhara' },
    ],
  },
  {
    xDefault: '/blog/mada-bayn-al-khotba-wa-al-zawaj',
    pages: [
      { hrefLang: 'ar', path: '/blog/mada-bayn-al-khotba-wa-al-zawaj' },
      { hrefLang: 'en', path: '/blog/how-long-should-an-islamic-engagement-last' },
      { hrefLang: 'de', path: '/blog/wie-lange-verlobung-im-islam' },
      { hrefLang: 'fr', path: '/blog/duree-fiancailles-islam' },
    ],
  },
];

// Non-Arabic standalone pages. No Arabic twin exists for any of these — see
// the "missing pairs" note at the bottom of this block.
const PAGE_LANG = {
  // Non-Arabic articles published by Foundelo (src/data/foundelo/).
  ...core.foundeloPageLang(foundeloArticles),
  // English articles
  '/blog/halal-dating-vs-islamic-marriage': 'en',
  '/blog/wefaqna-vs-muslim-dating-apps': 'en',
  '/blog/how-to-find-halal-spouse-online-2026': 'en',
  '/blog/talking-to-parents-about-marriage-muslim': 'en',
  '/blog/halal-marriage-guide-usa-canada': 'en',
  '/blog/halal-marriage-guide-uk': 'en',
  '/blog/nikah-vs-marriage-contract-explained': 'en',
  '/blog/is-online-muslim-marriage-halal': 'en',
  '/blog/muslim-marriage-app-comparison-2026': 'en',
  '/blog/istikhara-dua': 'en',
  '/blog/how-to-pray-istikhara': 'en',
  '/blog/how-long-should-an-islamic-engagement-last': 'en',
  // German articles
  '/blog/muslimische-ehe-in-deutschland': 'de',
  '/blog/halal-ehepartner-deutschland-finden': 'de',
  '/blog/istikhara-gebet': 'de',
  '/blog/wie-lange-verlobung-im-islam': 'de',
  // French articles
  '/blog/douaa-istikhara': 'fr',
  '/blog/duree-fiancailles-islam': 'fr',
};
//
// MISSING PAIRS (deliberately NOT declared — they are near-topics, not
// translations, and pairing them would tell Google to swap one for the other
// in results):
//   /blog/muslim-marriage-app-comparison-2026  ~  /blog/afdal-tatbeeqat-zawaj-shareeya-2026
//   /blog/wefaqna-vs-muslim-dating-apps        ~  /blog/farq-wefaqna-vs-other-apps
//   /blog/is-online-muslim-marriage-halal      ~  /blog/hal-al-taaruf-qabl-al-zawaj-halal
//   /blog/halal-marriage-guide-uk              ~  /blog/halal-marriage-guide-usa-canada
//                                                 (regional siblings, not translations)
// Any of these becomes a legitimate cluster the day someone writes a true
// translation; until then a self-reference is the honest annotation.

/**
 * Build the rel="alternate" set for a path.
 * Returns [] when there is nothing meaningful to say.
 */
export function buildHreflangLinks(path) {
  // Regional variants first, then true translations. A page belongs to at
  // most one set, so the order only decides which table is consulted first.
  const cluster =
    HREFLANG_CLUSTERS.find((c) => c.pages.some((p) => p.path === path)) ||
    TRANSLATION_CLUSTERS.find((c) => c.pages.some((p) => p.path === path));

  if (cluster) {
    const links = cluster.pages.map((p) => ({
      hrefLang: p.hrefLang,
      href: `${BASE_URL}${p.path}`,
    }));
    if (cluster.xDefault) {
      links.push({ hrefLang: 'x-default', href: `${BASE_URL}${cluster.xDefault}` });
    }
    return links;
  }

  // Standalone page: declare its own language, and mark it as its own
  // fallback since it is the only version that exists.
  const lang = PAGE_LANG[path] || DEFAULT_LANG;
  const self = `${BASE_URL}${path}`;
  return [
    { hrefLang: lang, href: self },
    { hrefLang: 'x-default', href: self },
  ];
}

/**
 * SEO component for per-page meta tags.
 * @param {string} title - Page title (suffix " | وفّقنا" added automatically)
 * @param {string} description - Meta description
 * @param {string} path - URL path (e.g. "/browse")
 * @param {boolean} noIndex - If true, adds noindex meta tag
 * @param {boolean} followLinks - Only meaningful with noIndex. Emits
 *   "noindex, follow" instead of the default "noindex, nofollow", i.e. keep the
 *   page out of the index but let Google keep crawling the links out of it.
 *   Use for a page that is a real destination in the site graph (e.g. /browse,
 *   which the /zawaj/* landings funnel into) rather than a dead-end account
 *   screen. Default stays false so every existing noIndex page is unchanged.
 * @param {string} [image] - Share-card image for og:image / twitter:image, as a
 *   site-relative path ("/og/bride.webp") or an absolute URL. Omit it and the
 *   page keeps the site-wide default card — see the "Share cards" block above
 *   for the 5 cluster images that still need to be produced.
 * @param {React.ReactNode} children - Optional extra head elements (e.g. structured data)
 */
const SEO = ({ title, description, path = '/', noIndex = false, followLinks = false, image, children }) => {
  const { i18n } = useTranslation();
  const brand = siteName(i18n.language);
  const fullTitle = title === SITE_NAME || title === brand
    ? title
    : `${title} | ${brand}`;
  const url = `${BASE_URL}${path}`;
  const shareImage = resolveShareImage(image);
  // hreflang on a noindex page is contradictory — the page is not eligible to
  // be swapped into anyone's results, so we say nothing about it.
  const hreflangLinks = noIndex ? [] : buildHreflangLinks(path);

  return (
    <Helmet>
      <title>{fullTitle}</title>
      <meta name="description" content={description} />
      <link rel="canonical" href={url} />

      {/* hreflang alternates — see HREFLANG_CLUSTERS / PAGE_LANG above. */}
      {hreflangLinks.map((link) => (
        <link
          key={link.hrefLang}
          rel="alternate"
          hrefLang={link.hrefLang}
          href={link.href}
        />
      ))}

      {noIndex && (
        <meta name="robots" content={followLinks ? 'noindex, follow' : 'noindex, nofollow'} />
      )}

      {/* Open Graph */}
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content={url} />
      <meta property="og:type" content="website" />
      <meta property="og:image" content={shareImage} />
      <meta property="og:locale" content="ar_EG" />
      <meta property="og:locale:alternate" content="ar_SA" />
      <meta property="og:locale:alternate" content="ar_AE" />
      <meta property="og:locale:alternate" content="ar_SY" />
      <meta property="og:locale:alternate" content="ar_MA" />
      <meta property="og:locale:alternate" content="ar_DZ" />
      <meta property="og:locale:alternate" content="en_US" />
      <meta property="og:locale:alternate" content="en_GB" />
      <meta property="og:site_name" content={SITE_NAME} />

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={shareImage} />

      {children}
    </Helmet>
  );
};

export default SEO;
